import Link from "next/link";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";

import IconPhone from "../assets/icons/icon-phone.svg";
import IconEnvelope from "../assets/icons/icon-envelope.svg";
import { BookTourFormContext } from "../hooks/useBookTourForm";

import Button from "../components/button";
import Hero from "../components/hero";
import Input from "../components/input";
import Textarea from "../components/textarea";
import BoardroomFormModal from "../components/boardroom-form-modal";
import MembershipFormModal from "../components/membership-form-modal";
import PhotoshootFormModal from "../components/photoshoot-form-modal";
import EventFormModal from "../components/event-form-modal";

import styles from "./contact.module.scss";
import TagManager from "react-gtm-module";

import SeoMeta from "../components/seo-meta";
import { activeCampaignSubmit } from "../lib/forms";
import useViewport, { Viewport } from "../hooks/useViewport";

// Active Campaign field names.
const fieldMap = {
  companyName: "customer_account",
  details: "field[9]"
};

export default function ContactPage({ locations, seo }) {
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showBoardroomModal, setShowBoardroomModal] = useState(false);
  const [showPhotoshootModal, setShowPhotoshootModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { setBookTourFormOpen } = useContext(BookTourFormContext);

  const { register, formState, handleSubmit, watch } = useForm({
    mode: "onChange",
    // Default Active Campaign values for this
    // specific form.
    defaultValues: {
      u: "29",
      f: "29",
      s: "",
      c: "0",
      m: "0",
      act: "sub",
      v: "2",
      or: "f5d84c2703e7b5d27d5178f33633a864"
    }
  });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "contact us",
        ...data
      }
    });

    activeCampaignSubmit(data)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };
  const onError = (e) => console.log(e);

  const { errors } = formState;

  const viewport = useViewport();

  return (
    <div className="container">
      <SeoMeta {...seo} />
      <Hero title="Contact Us" />

      <div className={styles.ContactFormWrapper}>
        <div className={styles.FormColumn}>
          <h3>We&apos;ll be in touch</h3>

          <p>
            One of our workspace experts will reach out to you based on your
            communication preferences. Submit the form below for any general
            inquiries and we’ll be in touch with additional details.
          </p>

          {submitted && (
            <p className={styles.ConfirmationMessage}>
              Thank you, we will be in touch shortly!
            </p>
          )}

          {!submitted && (
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <div className="form-group-inline">
                <Input
                  type="text"
                  placeholder="First name *"
                  {...register("firstname", {
                    required: "First name is required"
                  })}
                  errorText={errors.firstname && errors.firstname.message}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Last name *"
                  {...register("lastname", {
                    required: "Last name is required"
                  })}
                  errorText={errors.lastname && errors.lastname.message}
                  inline
                />
              </div>
              <Input
                type="text"
                placeholder="Work email address *"
                {...register("email", { required: "Email is required" })}
                errorText={errors.email && errors.email.message}
              />

              <Input
                type="text"
                placeholder="Phone number *"
                {...register("phone", {
                  required: "Phone number is required"
                })}
                errorText={errors.phone && errors.phone.message}
              />

              <Input
                type="text"
                placeholder="Company name *"
                {...register(fieldMap.companyName, {
                  required: "Company name is required"
                })}
                errorText={
                  errors[fieldMap.companyName] &&
                  errors[fieldMap.companyName].message
                }
              />

              <Textarea
                type="text"
                placeholder="What can we help you with? *"
                rows="1"
                {...register(fieldMap.details, {
                  required: "Details are required"
                })}
                errorText={
                  errors[fieldMap.details] && errors[fieldMap.details].message
                }
              />

              {error && <p>{error}</p>}

              <div className={styles.ButtonWrapper}>
                <Button rounded outline small type="submit">
                  Get in Touch
                </Button>
              </div>
            </form>
          )}

          <div className={styles.SecondForm}>
            <h3>Already know what you&apos;re looking for?</h3>

            <p>
              Submit a request and one of our workspace experts will reach out
              to you with additional details for the related inquiry.
            </p>
            <div className={styles.SelectOptions}>
              <div className="flex flex-rpw justify-between items-start gap-3 mb-3">
                <Button
                  outline
                  className="w-full text-left"
                  onClick={() => setShowMembershipModal(true)}
                >
                  Membership
                </Button>
                <Button
                  outline
                  className="w-full text-left"
                  onClick={() => setShowBoardroomModal(true)}
                >
                  Boardroom Booking
                </Button>
              </div>
              <div className="flex flex-row justify-between items-start gap-3">
                <Button
                  outline
                  className="w-full text-left"
                  onClick={() => setShowPhotoshootModal(true)}
                >
                  Photo / Video Shoot
                </Button>
                <Button
                  outline
                  className="w-full text-left"
                  onClick={() => setShowEventModal(true)}
                >
                  Event Space
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.HelpColumn}>
          <div>
            <h3>Connect Directly</h3>

            <p>
              Send us an email or give us a call and one of our workspace
              experts are happy to discuss your questions directly.
            </p>

            <div className="pt-[47px]"></div>

            <div className={styles.PhoneNumber}>
              <div className="w-6">
                <IconEnvelope role="presentation" />
              </div>
              <a className="text-link" href="mailto:info@eastroom.com">
                info@eastroom.com
              </a>
            </div>

            <div className="py-1"></div>

            <div className={styles.PhoneNumber}>
              <div className="w-6">
                <IconPhone role="presentation" />
              </div>
              <a className="text-link" href="tel:+14166285333">
                (416) 628 - 5333
              </a>
            </div>
          </div>

          <div className={styles.BottomQuestions}>
            <section>
              <h3>Not ready to talk to someone?</h3>
              <p>
                Take a look at our{" "}
                <Link href="/faq" passHref>
                  <a className="underline">FAQ Page.</a>
                </Link>
              </p>
            </section>

            <section>
              <h3>Skip the questions</h3>
              <p>
                <a
                  className="underline"
                  onClick={() => setBookTourFormOpen(true)}
                >
                  Book a tour
                </a>{" "}
                for an in person viewing.
              </p>
            </section>
          </div>
        </div>
      </div>

      {showBoardroomModal && (
        <BoardroomFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showBoardroomModal}
          onClose={() => setShowBoardroomModal(false)}
          onRequestClose={() => setShowBoardroomModal(false)}
        />
      )}

      {showMembershipModal && (
        <MembershipFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showMembershipModal}
          onRequestClose={() => setShowMembershipModal(false)}
        />
      )}

      {showPhotoshootModal && (
        <PhotoshootFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showPhotoshootModal}
          onRequestClose={() => setShowPhotoshootModal(false)}
        />
      )}

      {showEventModal && (
        <EventFormModal
          lightbox={viewport !== Viewport.PHONE}
          locations={locations}
          isOpen={showEventModal}
          onRequestClose={() => setShowEventModal(false)}
        />
      )}
    </div>
  );
}

export async function getServerSideProps() {
  try {
    const [
      { getLocations },
      { getSEOPageProps }
    ] = await Promise.all([
      import("../lib/locations"),
      import("../lib/seo")
    ]);

    const [locations, seo] = await Promise.all([
      getLocations(),
      getSEOPageProps({ resolvedUrl: "/contact" })
    ]);

    return {
      props: {
        locations,
        seo
      },
    };
  } catch (e) {
    console.log(e.message);
  }
}
